import { useMemo, useState } from "react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { ChevronsLeftRight } from "lucide-react";
import { Reveal, PageHeader } from "@/components/Reveal";
import { BeforeAfterSlider } from "@/components/BeforeAfterSlider";
import { useContent } from "@/context/ContentContext";

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");

export default function Gallery() {
  const { content } = useContent();
  const items = content?.gallery || [];
  const [filter, setFilter] = useState("All");
  const [lightbox, setLightbox] = useState(null);
  const vis = (k) => content?.settings?.section_visibility?.[k] ?? true;

  const categories = useMemo(() => ["All", ...new Set(items.map((g) => g.category))], [items]);
  const filtered = filter === "All" ? items : items.filter((g) => g.category === filter);
  const comparisons = filtered.filter((g) => g.before_image && g.after_image);
  const photos = filtered.filter((g) => !(g.before_image && g.after_image));

  return (
    <div className="px-4 pb-24 pt-44 sm:px-8 lg:px-16" data-testid="gallery-page">
      <PageHeader
        eyebrow="The Results Gallery"
        title="Proof, Not Promises"
        subtitle="Drag the gold handle on any comparison to reveal the transformation — then explore the clinic behind the results."
        testid="gallery-title"
      />

      <Reveal delay={0.25} className="mt-12 flex justify-center">
        <div className="glass flex flex-wrap justify-center gap-1 rounded-full p-1.5" role="tablist" aria-label="Gallery categories">
          {categories.map((c) => (
            <button
              key={c}
              role="tab"
              aria-selected={filter === c}
              onClick={() => setFilter(c)}
              data-testid={`gallery-tab-${slug(c)}`}
              className={`rounded-full px-5 py-2.5 text-xs font-medium tracking-wide transition-colors duration-300 ${
                filter === c ? "bg-gradient-to-r from-[#D4AF37] to-[#AA7C11] text-[#1A1615]" : "text-[#5A504B] hover:text-[#B38B2D]"
              }`}
            >
              {c}
            </button>
          ))}
        </div>
      </Reveal>

      {comparisons.length > 0 && vis("gallery_before_after") && (
        <div className="mx-auto mt-16 max-w-6xl">
          <Reveal>
            <div className="flex items-center gap-3">
              <ChevronsLeftRight className="h-4 w-4 text-[#B38B2D]" aria-hidden="true" />
              <h2 className="font-serif text-2xl font-semibold text-[#1A1615]">Before &amp; After</h2>
              <span className="text-xs font-light text-[#A39288]">— drag to compare</span>
            </div>
          </Reveal>
          <div className="mt-8 grid gap-10 md:grid-cols-2" data-testid="before-after-grid">
            {comparisons.map((g, i) => (
              <Reveal key={g.id} delay={(i % 2) * 0.1}>
                <figure data-testid={`gallery-comparison-${g.id}`}>
                  <BeforeAfterSlider before={g.before_image} after={g.after_image} title={g.title} />
                  <figcaption className="mt-4 px-1">
                    <p className="text-[10px] font-medium uppercase tracking-[0.22em] text-[#B38B2D]">{g.category}</p>
                    <h3 className="mt-1 font-serif text-xl font-semibold text-[#1A1615]">{g.title}</h3>
                    <p className="mt-1.5 text-sm font-light text-[#5A504B]">{g.description}</p>
                  </figcaption>
                </figure>
              </Reveal>
            ))}
          </div>
        </div>
      )}

      {photos.length > 0 && vis("gallery_photos") && (
        <div className="mx-auto mt-16 max-w-6xl">
          {comparisons.length > 0 && vis("gallery_before_after") && (
            <Reveal>
              <h2 className="font-serif text-2xl font-semibold text-[#1A1615]">Inside the Clinic</h2>
            </Reveal>
          )}
          <div className="mt-8 grid gap-6 md:grid-cols-2 lg:grid-cols-3" data-testid="gallery-grid">
            {photos.map((g, i) => (
              <Reveal key={g.id} delay={(i % 3) * 0.08}>
                <button
                  onClick={() => setLightbox(g)}
                  data-testid={`gallery-item-${g.id}`}
                  className="group relative block w-full overflow-hidden rounded-[2rem] text-left focus:outline-none focus:ring-2 focus:ring-[#D4AF37]"
                >
                  <img src={g.image} alt={g.title} loading="lazy" className="aspect-[4/3] w-full object-cover transition-transform duration-700 ease-liquid group-hover:scale-105" />
                  <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-[#1A1615]/85 via-[#1A1615]/30 to-transparent p-6 pt-16">
                    <p className="text-[10px] font-medium uppercase tracking-[0.22em] text-[#F3E5AB]">{g.category}</p>
                    <h3 className="mt-1 font-serif text-xl font-semibold text-white">{g.title}</h3>
                  </div>
                </button>
              </Reveal>
            ))}
          </div>
        </div>
      )}

      <Dialog open={!!lightbox} onOpenChange={() => setLightbox(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto border-white/60 bg-[#FFFDF9]/95 backdrop-blur-xl sm:max-w-2xl" data-testid="gallery-lightbox">
          <DialogTitle className="sr-only">{lightbox?.title || "Gallery image"}</DialogTitle>
          {lightbox && (
            <div>
              <img src={lightbox.image} alt={lightbox.title} className="w-full rounded-2xl object-cover" />
              <p className="eyebrow mt-5">{lightbox.category}</p>
              <h3 className="mt-2 font-serif text-2xl font-semibold text-[#1A1615]">{lightbox.title}</h3>
              <p className="mt-2 text-sm font-light leading-relaxed text-[#5A504B]">{lightbox.description}</p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
